import { FilterDateDashboard } from "../../../../components/Dashboard/ViewDatas/FilterDate";
import { ReactComponent as BagFood } from "../../../../assets/svg/icons/bagFood.svg";
import { DateModal } from "../../../../components/Modal/DateFilter";
import { useFilter } from "../../../../hooks/useFilter";
import {
	selectionToNumericIds,
	selectionToSortedIdsKey,
	useFilterContextV2,
} from "../../../../contexts/FilterContextV2";
import Constants from "../../../../constants/Index";
import axios from "../../../../services/axios"
import { useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import { IOptionProps } from "../../../../@types/reactSelect/IOption";
import { exportSupplementationAnalysisToExcel } from "../../../../utils/excel/exportSupplementationAnalysis";

export default function DashboardSupplementationProductReport() {
  const defaultValues = {
    id: 0,
    supplement_classification_id: 0,
    supplement_classification: "-",
    trade_name: "-",
    total_consumption_period: 0,
    analyzed_period: 0,
    supplemented_animals: 0,
    average_weight_of_supplemented_animals: 0,
    total_weight_of_supplemented_animals: 0,
    uas_total_number_of_animals_supplemented: 0,
    average_consumption_in_the_period_kg: 0,
    average_consumption_in_the_period_kg_pct: 0,
    average_consumption_in_the_period_ua: 0,
    average_consumption_in_the_period_ua_pct: 0,
    replacement_frequency: 0,
    replacement_frequency_pct: 0
  }

  const {
    rangeDays,
    setRangeDays,
    setHideMicroAreaFilter,
    setHideBatchFilter,
    setDashboard,
    setSideBarOptionSelected,
    setSelectRangeDate
  } = useFilter();

  const { selectedFarms, selectedAreas } = useFilterContextV2();

  const farmsKey = useMemo(
    () => selectionToSortedIdsKey(selectedFarms),
    [selectedFarms],
  );
  const areasKey = useMemo(
    () => selectionToSortedIdsKey(selectedAreas),
    [selectedAreas],
  );

  useEffect(() => {
    setHideMicroAreaFilter(true)
    setHideBatchFilter(true)
    setSideBarOptionSelected("DASHBOARD")
    setDashboard("SUPPLEMENTATION")
    setSelectRangeDate(0)
    document.title = Constants.TITLE_DASHBOARD;

    if (!rangeDays) {
      const now = new Date();
      const firstDayOfCurrentMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const firstDayOfLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      setRangeDays({ from: firstDayOfLastMonth, to: firstDayOfCurrentMonth });
    }
  }, []);

  const IconMarq = (value: number, min: number, max: number) => {
    let hex = "FFFFFF";

    if (value < min) {
      hex = "00FF00"; // Verde: value < min
    } else if (value >= min && value <= max) {
      hex = "FFFF00"; // Amarelo: min <= value <= max
    } else {
      hex = "FF0000"; // Vermelho: value > max
    }

    return (
      <div style={{
        width: 20,
        height: 20,
        background: `#${hex}`,
        borderRadius: 50,
        border: "1px solid #FFF"
      }} />
    )
  }

  const LegendaTable = (title: string, min: number, max: number) => {

    return (
      <div className="tableDashboard" id={"estoque_suple"}>
        <table>
          <thead style={{
            color: "#fff"
          }}>
            <th scope="col" rowSpan={2} style={{
              background: "#324440",
              width: "180px",
              textAlign: "left"
            }}>
              {title}
            </th>

            {[{
              legenda: `<${min}%`
            },
            {
              legenda: `>=${min}% <=${max}%`
            },
            {
              legenda: `>${max}%`
            }
            ].map((item, index) => (
              <th key={index} scope="col" rowSpan={2} style={{
                background: "#324440",
                width: "150px",
                textAlign: "left"
              }}>
                {item.legenda}
              </th>
            ))}
          </thead>

          <tbody>
            <tr className="bg_white">
              <th scope="row"></th>
              <td style={{
                textAlign: "left"
              }}>{IconMarq(min - 1, min, max)}</td>

              <td style={{
                textAlign: "left"
              }}>{IconMarq((min + max) / 2, min, max)}</td>

              <td style={{
                textAlign: "left"
              }}>{IconMarq(max + 1, min, max)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    )
  }

  const [analysisProduct, setAnalysisProduct] = useState<ISupplementAnalysisProduct[]>([defaultValues]);
  const [analysisBatch, setAnalysisBatch] = useState<ISupplementAnalysisProduct[]>([]);

  useEffect(() => {
    const farmIds = selectionToNumericIds(selectedFarms);
    const areaIds = selectionToNumericIds(selectedAreas);
    if (!farmIds.length || !areaIds.length || !rangeDays) return;

    setAnalysisProduct([]);
    setAnalysisBatch([]);

    const controller = new AbortController();

    const fetchAllData = async () => {
      const startDate = format(rangeDays.from ? new Date(rangeDays.from) : new Date(), 'yyyy-MM-dd HH:mm:ss');
      const endDate = format(rangeDays.to ? new Date(rangeDays.to) : new Date(), 'yyyy-MM-dd HH:mm:ss');

      const params = {
        farmIds,
        areaIds,
        startDate: startDate,
        endDate: endDate
      };

      const paramsSerializer = (params: any) => new URLSearchParams(params).toString();

      try {
        const [productRes, batchRes] = await Promise.all([
          axios.get("/supplement/analysis-by-product", {
            params,
            paramsSerializer,
            signal: controller.signal
          }),
          axios.get("/supplement/analysis-batch-by-product", {
            params,
            paramsSerializer,
            signal: controller.signal
          })
        ]);

        if (productRes.data.length > 0) {
          const sortedProducts = [...productRes.data];
          sortedProducts.sort((a: ISupplementAnalysisProduct, b: ISupplementAnalysisProduct) => {
            const aKey = a.batch_initials || "";
            const bKey = b.batch_initials || "";
            return aKey.localeCompare(bKey, undefined, { sensitivity: "base" });
          });
          setAnalysisProduct(sortedProducts);
        }
        if (batchRes.data.length > 0) {
          const sortedBatches = [...batchRes.data];
          sortedBatches.sort((a: ISupplementAnalysisProduct, b: ISupplementAnalysisProduct) => {
            const aKey = a.batch_initials || "";
            const bKey = b.batch_initials || "";
            return aKey.localeCompare(bKey, undefined, { sensitivity: "base" });
          });
          setAnalysisBatch(sortedBatches);
        }

      } catch (error) {
        if (!controller.signal.aborted) {
          console.error('Erro ao carregar análise de produto:', error);
        }
      }
    };

    fetchAllData();

    return () => controller.abort();
  }, [rangeDays, farmsKey, areasKey]);

  const tableProduct = (products: ISupplementAnalysisProduct[]) => {
    const haveBatch = products.some((item) => item.batch_initials);

    const renderTableHeader = () => (
      <>
        {haveBatch ?
          <>
            <br />
            <h2>Consumo por lote</h2>
          </> : <></>}

        <thead style={{ color: "#fff" }}>
          <tr>
            <th scope="col" style={{ background: "#808080", width: 150, textAlign: "left" }} hidden={!haveBatch}>Lote</th>
            <th scope="col" style={{ background: "#324440", width: haveBatch ? 230 : 380, textAlign: haveBatch ? "center" : "left" }}>Nome do Produto</th>
            {[
              "Classificação do Produto",
              "Consumo Total no Período (Kg)",
              "Período Analisado (dias)",
              "Animais Suplementados",
              "Peso Médio dos Animais Suplementados (Kg)",
              "Peso Total dos Animais Suplementados (Kg)",
              "Consumo Médio no Período (g/kgPV.dia)",
              "Consumo Médio no Período (g.UA/dia)",
              "Frequência de Reposição (Média de Dias)"
            ].map((title, index) => (
              <th key={index} scope="col" style={{
                background: index % 2 === 0 ? "#808080" : "#324440",
                padding: "10px 20px",
                width: 150
              }}>
                {title}
              </th>
            ))}
          </tr>
        </thead>
      </>
    );

    const renderTableBody = () => (
      <tbody>
        {products.map((item, index) => (
          <tr key={index} className={index % 2 === 0 ? "bg_white" : "bg_gray"}>
            <th hidden={!haveBatch}>{item.batch_initials}</th>
            <th style={{ textAlign: haveBatch ? "right" : "left" }}>{item.trade_name}</th>
            <td>{item.supplement_classification}</td>
            <td>{item.total_consumption_period}</td>
            <td>{item.analyzed_period}</td>
            <td>{item.supplemented_animals}</td>
            <td>{item.average_weight_of_supplemented_animals}</td>
            <td>{item.total_weight_of_supplemented_animals}</td>
            <td>
              <div style={{ width: "100%", display: "flex", justifyContent: "space-between" }}>
                {(() => {
                  const hasValue = item.average_consumption_in_the_period_kg != null &&
                    item.average_consumption_in_the_period_kg_pct != null;
                  return hasValue ? (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        position: "relative",
                        cursor: "pointer"
                      }}
                      onMouseEnter={(e) => {
                        const tooltip = e.currentTarget.querySelector('.tooltip-consumption-kg') as HTMLElement;
                        if (tooltip) {
                          tooltip.style.visibility = 'visible';
                          tooltip.style.opacity = '1';
                        }
                      }}
                      onMouseLeave={(e) => {
                        const tooltip = e.currentTarget.querySelector('.tooltip-consumption-kg') as HTMLElement;
                        if (tooltip) {
                          tooltip.style.visibility = 'hidden';
                          tooltip.style.opacity = '0';
                        }
                      }}
                    >
                      {IconMarq(item.average_consumption_in_the_period_kg_pct, 5, 15)}
                      {item.average_consumption_in_the_period_kg}
                      <span
                        className="tooltip-consumption-kg"
                        style={{
                          visibility: "hidden",
                          width: "auto",
                          backgroundColor: "#fff",
                          color: "#000",
                          padding: "4px 8px",
                          borderRadius: "5px",
                          position: "absolute",
                          bottom: "100%",
                          left: "50%",
                          transform: "translateX(-50%)",
                          marginBottom: "5px",
                          zIndex: 1000,
                          whiteSpace: "nowrap",
                          fontSize: "0.8rem",
                          fontFamily: "Fira Sans",
                          boxShadow: "0 2px 5px rgba(0,0,0,0.2)",
                          pointerEvents: "none",
                          opacity: 0,
                          transition: "opacity 0.3s, visibility 0.3s"
                        }}
                      >
                        {item.average_consumption_in_the_period_kg_pct}%
                      </span>
                    </div>
                  ) : "-";
                })()}
              </div>
            </td>
            {/* <td>{item.average_consumption_in_the_period_kg_pct}%</td> */}
            <td>
              <div style={{ width: "100%", display: "flex", justifyContent: "space-between" }}>
                {(() => {
                  const hasValue = item.average_consumption_in_the_period_ua != null &&
                    item.average_consumption_in_the_period_ua_pct != null;
                  return hasValue ? (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        position: "relative",
                        cursor: "pointer"
                      }}
                      onMouseEnter={(e) => {
                        const tooltip = e.currentTarget.querySelector('.tooltip-consumption-ua') as HTMLElement;
                        if (tooltip) {
                          tooltip.style.visibility = 'visible';
                          tooltip.style.opacity = '1';
                        }
                      }}
                      onMouseLeave={(e) => {
                        const tooltip = e.currentTarget.querySelector('.tooltip-consumption-ua') as HTMLElement;
                        if (tooltip) {
                          tooltip.style.visibility = 'hidden';
                          tooltip.style.opacity = '0';
                        }
                      }}
                    >
                      {IconMarq(item.average_consumption_in_the_period_ua_pct, 5, 15)}
                      {item.average_consumption_in_the_period_ua}
                      <span
                        className="tooltip-consumption-ua"
                        style={{
                          visibility: "hidden",
                          width: "auto",
                          backgroundColor: "#fff",
                          color: "#000",
                          padding: "4px 8px",
                          borderRadius: "5px",
                          position: "absolute",
                          bottom: "100%",
                          left: "50%",
                          transform: "translateX(-50%)",
                          marginBottom: "5px",
                          zIndex: 1000,
                          whiteSpace: "nowrap",
                          fontSize: "0.8rem",
                          fontFamily: "Fira Sans",
                          boxShadow: "0 2px 5px rgba(0,0,0,0.2)",
                          pointerEvents: "none",
                          opacity: 0,
                          transition: "opacity 0.3s, visibility 0.3s"
                        }}
                      >
                        {item.average_consumption_in_the_period_ua_pct}%
                      </span>
                    </div>
                  ) : "-";
                })()}
              </div>
            </td>
            {/* <td>{item.average_consumption_in_the_period_ua_pct}%</td> */}
            <td>
              <div style={{ width: "100%", display: "flex", justifyContent: "space-between" }}>
                {(() => {
                  const hasValue = item.replacement_frequency != null &&
                    item.replacement_frequency !== 0 &&
                    item.replacement_frequency_pct != null &&
                    item.supplemented_animals > 0;
                  return hasValue ? (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        position: "relative",
                        cursor: "pointer"
                      }}
                      onMouseEnter={(e) => {
                        const tooltip = e.currentTarget.querySelector('.tooltip-replacement-frequency') as HTMLElement;
                        if (tooltip) {
                          tooltip.style.visibility = 'visible';
                          tooltip.style.opacity = '1';
                        }
                      }}
                      onMouseLeave={(e) => {
                        const tooltip = e.currentTarget.querySelector('.tooltip-replacement-frequency') as HTMLElement;
                        if (tooltip) {
                          tooltip.style.visibility = 'hidden';
                          tooltip.style.opacity = '0';
                        }
                      }}
                    >
                      {IconMarq(item.replacement_frequency_pct, 15, 30)}
                      {item.replacement_frequency}
                      <span
                        className="tooltip-replacement-frequency"
                        style={{
                          visibility: "hidden",
                          width: "auto",
                          backgroundColor: "#fff",
                          color: "#000",
                          padding: "4px 8px",
                          borderRadius: "5px",
                          position: "absolute",
                          bottom: "100%",
                          left: "50%",
                          transform: "translateX(-50%)",
                          marginBottom: "5px",
                          zIndex: 1000,
                          whiteSpace: "nowrap",
                          fontSize: "0.8rem",
                          fontFamily: "Fira Sans",
                          boxShadow: "0 2px 5px rgba(0,0,0,0.2)",
                          pointerEvents: "none",
                          opacity: 0,
                          transition: "opacity 0.3s, visibility 0.3s"
                        }}
                      >
                        {item.replacement_frequency_pct}%
                      </span>
                    </div>
                  ) : "-";
                })()}
              </div>
            </td>
            {/* <td>{item.replacement_frequency_pct}%</td> */}
          </tr>
        ))}
      </tbody>
    );

    return (
      <div className="tableDashboard">
        <table>
          {renderTableHeader()}
          {renderTableBody()}
        </table>
      </div>
    );
  }

  return (
    <div className="cattleMap_background" style={{ height: "100vh", overflowY: "auto" }}>
      <DateModal />

      <form className="dashboardPastureManagement">
        <div className="cattleMap_page">
          <div className="cattleMap_header">
            <BagFood height={72} />
            <div style={{
              display: "flex",
              flexDirection: "column",
            }}>
              <h2 className="cattleMap_title">
                Suplementação
              </h2>
              <h3 className="cattleMap_subtitle">
                Análise de consumo
              </h3>
            </div>
          </div>

          <FilterDateDashboard
            onExportExcel={() => {
              if ((analysisProduct || analysisBatch) && (analysisProduct.length > 0 || analysisBatch.length > 0)) {
                try {
                  exportSupplementationAnalysisToExcel(
                    analysisProduct || [],
                    analysisBatch || [],
                    rangeDays?.from,
                    rangeDays?.to
                  );
                } catch (error) {
                  console.error('Erro ao exportar:', error);
                  alert('Erro ao exportar dados. Tente novamente.');
                }
              }
            }}
          />

          {
            <div className="tableDashboard" id={"estoque_suple"}>
              {tableProduct(analysisProduct)}

              {analysisBatch.length != 0 ? tableProduct(analysisBatch) : <></>}
              <br />
              <div style={{
                width: "100%",
                display: "flex",
                flexDirection: "column",
                gap: 10
              }}>
                {LegendaTable("Legenda Consumo", 5, 15)}

                {LegendaTable("Legenda Frequência", 15, 30)}
              </div>
            </div>
          }
        </div>
      </form>
    </div>
  );
}
